import { useRefs } from 'contexts/RefContext';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import Konva from 'konva';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addFlattenDraftPoint, FlattenArea, setFlattenPolygon } from 'store/slices/TreeSlice';
import { selectIsFlattenAreaMode, selectFlattenShape } from 'store/slices/TreeSlice/selector';
import DraftHandles from './DraftHandles';
import { POLYGON_DASH, POLYGON_STROKE, POLYGON_STROKE_WIDTH } from './FlattenAreaPolygon';
import {
	getImagePointFromEvent, shouldBlockNewShape, useFlattenAreaGuardRefs, useLatestRef, usePinchAwareTap,
} from './flattenAreaToolUtils';
import { pointsToPolygon } from 'components/tool/utils';

const CLOSE_DISTANCE_PX = 10;

export default function FlattenAreaPolygonTool() {
	const dispatch = useDispatch();
	const { konvaRef } = useRefs();
	const baseImageScale = useKonvaImageScale();

	const { treeEditingMode } = useSelector((state: RootState) => state.treeSlice.data);
	const { moveStage, rotateStage, editModeEnabled, threeDModelEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const isFlattenAreaMode = useSelector(selectIsFlattenAreaMode);
	const flattenShape = useSelector(selectFlattenShape);
	const draftPoints = useSelector((state: RootState) => state.treeSlice.data.draftFlatten.points);
	const { selectedShapeIdRef, flattenAreaPolygonsRef, loadingFlattenIdsRef } = useFlattenAreaGuardRefs();

	const [previewPoint, setPreviewPoint] = useState<{ x: number; y: number } | null>(null);
	const drawingIdRef = useRef<string | null>(null);
	const draftPointsRef = useLatestRef(draftPoints);

	const canEdit = editModeEnabled && treeEditingMode && isFlattenAreaMode && !threeDModelEnabled && flattenShape === 'polygon';
	const canDraw = canEdit && !moveStage && !rotateStage;

	const getImagePoint = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => (
		getImagePointFromEvent(e, baseImageScale)
	), [baseImageScale]);

	const resetLocal = useCallback(() => {
		drawingIdRef.current = null;
		setPreviewPoint(null);
	}, []);

	// The actual tap action: place a vertex, or close the polygon if near the start point.
	const performTap = useCallback((point: { x: number; y: number }) => {
		const currentPoints = draftPointsRef.current;
		if (currentPoints.length === 0) {
			if (shouldBlockNewShape(
				dispatch, point, selectedShapeIdRef.current, flattenAreaPolygonsRef.current, loadingFlattenIdsRef.current
			)) return;
			drawingIdRef.current = crypto.randomUUID();
			dispatch(addFlattenDraftPoint({ point }));
			setPreviewPoint(point);
			return;
		}

		if (currentPoints.length >= 6) {
			const startX = currentPoints[0];
			const startY = currentPoints[1];
			const dist = Math.hypot(point.x - startX, point.y - startY);
			if (dist < CLOSE_DISTANCE_PX) {
				const id = drawingIdRef.current ?? crypto.randomUUID();
				const polygonCoords = pointsToPolygon(currentPoints);
				const flattenAreaPolygon: FlattenArea = {
					coords: polygonCoords,
					id,
					flat_px: null,
					facet_affected_px: null,
					ground_z: 0,
					height: 0,
					shape: 'polygon',
				};

				dispatch(setFlattenPolygon(flattenAreaPolygon));
				resetLocal();
				return;
			}
		}

		dispatch(addFlattenDraftPoint({ point }));
		setPreviewPoint(point);
	}, [dispatch, resetLocal, selectedShapeIdRef, draftPointsRef, flattenAreaPolygonsRef, loadingFlattenIdsRef]);

	const {
		isPinchingRef, isConcurrentFinger, startTap, clearPointerUp,
		handleTouchStart, handleTouchMove, handleTouchEnd,
	} = usePinchAwareTap(performTap);

	const handlePointerDown = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (!canDraw) return;
		if (isConcurrentFinger(e)) return; // 2nd, concurrent finger — polygon stays exactly as is
		const point = getImagePoint(e);
		if (!point) return;
		startTap(e, point);
	}, [canDraw, getImagePoint, isConcurrentFinger, startTap]);

	const handlePointerMove = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		const currentPoints = draftPointsRef.current;
		if (!currentPoints.length) return;
		if (!canDraw) return;
		if (isPinchingRef.current) return; // pinch — ignore its move noise
		let point = getImagePoint(e);
		if (!point) return;

		if (currentPoints.length >= 6) {
			const startX = currentPoints[0];
			const startY = currentPoints[1];
			const dist = Math.hypot(point.x - startX, point.y - startY);
			if (dist < CLOSE_DISTANCE_PX) {
				point = { x: startX, y: startY };
			}
		}

		setPreviewPoint(point);
	}, [canDraw, getImagePoint, draftPointsRef, isPinchingRef]);

	const handlePointerUp = clearPointerUp;

	useEffect(() => {
		const stage = konvaRef?.current;
		if (!stage || !canDraw) return;

		stage.on('pointerdown', handlePointerDown);
		stage.on('pointermove', handlePointerMove);
		stage.on('pointerup', handlePointerUp);
		stage.on('touchstart', handleTouchStart);
		stage.on('touchmove', handleTouchMove);
		stage.on('touchend', handleTouchEnd);
		stage.on('touchcancel', handleTouchEnd);

		return () => {
			stage.off('pointerdown', handlePointerDown);
			stage.off('pointermove', handlePointerMove);
			stage.off('pointerup', handlePointerUp);
			stage.off('touchstart', handleTouchStart);
			stage.off('touchmove', handleTouchMove);
			stage.off('touchend', handleTouchEnd);
			stage.off('touchcancel', handleTouchEnd);
		};
	}, [
		canDraw, handlePointerDown, handlePointerMove, handlePointerUp,
		handleTouchStart, handleTouchMove, handleTouchEnd, konvaRef,
	]);

	useEffect(() => {
		if (canEdit) return;
		resetLocal();
	}, [canEdit, resetLocal]);

	useEffect(() => () => resetLocal(), [resetLocal]);

	const linePoints = useMemo(() => {
		if (!previewPoint) return draftPoints;
		return [...draftPoints, previewPoint.x, previewPoint.y];
	}, [previewPoint, draftPoints]);

	if (!canEdit) return null;

	return draftPoints.length > 0 ? (
		<>
			<Line
				points={linePoints}
				stroke={POLYGON_STROKE}
				strokeWidth={POLYGON_STROKE_WIDTH}
				dash={POLYGON_DASH}
				strokeScaleEnabled={false}
				closed={false}
				listening={false}
			/>
			<DraftHandles points={draftPoints} />
		</>
	) : null;
}
